import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DelegationsContextToolbar } from '../../components/delegations/DelegationsContextToolbar';
import {
  AdminFilterSearch,
  AdminFilterSelect,
  AdminFiltersPanel,
} from '../../components/admin/AdminDataFilters';
import { formatDateTimeEsCO } from '../../utils/dateTime';
import LoadingState, { Spinner } from '../../components/common/LoadingState';
import { useConfirm } from '../../components/confirm/ConfirmDialogProvider';
import { delegationService } from '../../services/delegationService';
import {
  DelegationStatus,
  ManagedAssemblyDelegationsSummary,
  VotingDelegation,
} from '../../types/delegation';
import { notifyError, notifySuccess, notifyWarning } from '../../utils/notificationCenter';
import {
  Ban,
  CheckCircle2,
  Clock,
  Download,
  Handshake,
  Percent,
  Scale,
  Users,
  XCircle,
} from 'lucide-react';

interface DelegationsManagementPageProps {
  mode: 'admin' | 'manager' | 'builder';
  /** Si es powers, resalta descarga y filtra por aceptadas por defecto (admin). */
  focus?: 'default' | 'powers';
}

const emptySummary: ManagedAssemblyDelegationsSummary = {
  totalCoefficientAccepted: 0,
  totalCoefficientPending: 0,
  totalCoefficientRevoked: 0,
  totalCoefficientRejected: 0,
  activePowersCount: 0,
  uniqueDelegatorsAccepted: 0,
  uniqueDelegatesAccepted: 0,
  complexTotalCoefficient: 0,
  delegationCoveragePercent: 0,
};

const splitTowerAndApartment = (apartmentValue?: string): { tower: string; apartment: string } => {
  if (!apartmentValue) return { tower: '', apartment: '' };
  const normalized = apartmentValue.split(',')[0]?.trim() || '';
  const separatorIndex = normalized.indexOf('-');
  if (separatorIndex <= 0) {
    return { tower: '', apartment: normalized };
  }
  return {
    tower: normalized.slice(0, separatorIndex).trim(),
    apartment: normalized.slice(separatorIndex + 1).trim(),
  };
};

const formatUnitChips = (apartmentValue?: string) => {
  if (!apartmentValue) return null;
  const units = apartmentValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  return units.map((unit) => {
    const { tower, apartment } = splitTowerAndApartment(unit);
    return (
      <span
        key={unit}
        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
      >
        {tower ? `T${tower}` : ''}
        {tower && apartment ? ' · ' : ''}
        {apartment ? `Apt ${apartment}` : unit}
      </span>
    );
  });
};

const statusConfig: Record<
  DelegationStatus,
  { label: string; className: string; icon: React.ComponentType<{ className?: string }> }
> = {
  [DelegationStatus.PENDING]: {
    label: 'Pendiente',
    className: 'bg-amber-50 text-amber-700',
    icon: Clock,
  },
  [DelegationStatus.ACCEPTED]: {
    label: 'Aceptada',
    className: 'bg-green-50 text-green-700',
    icon: CheckCircle2,
  },
  [DelegationStatus.REJECTED]: {
    label: 'Rechazada',
    className: 'bg-rose-50 text-rose-700',
    icon: XCircle,
  },
  [DelegationStatus.REVOKED]: {
    label: 'Revocada',
    className: 'bg-slate-100 text-slate-600',
    icon: Ban,
  },
};

const formatCoefficient = (value: number): string =>
  value.toLocaleString('es-CO', { maximumFractionDigits: 6 });

export const DelegationsManagementPage: React.FC<DelegationsManagementPageProps> = ({
  mode,
  focus = 'default',
}) => {
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [loadingDelegations, setLoadingDelegations] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [complexes, setComplexes] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedComplexId, setSelectedComplexId] = useState('');
  const [complexName, setComplexName] = useState<string | null>(null);
  const [assemblies, setAssemblies] = useState<
    Array<{ id: string; title: string; scheduledDate?: string }>
  >([]);
  const [selectedAssemblyId, setSelectedAssemblyId] = useState('');
  const [delegations, setDelegations] = useState<VotingDelegation[]>([]);
  const [summary, setSummary] = useState<ManagedAssemblyDelegationsSummary>(emptySummary);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const powersFocus = mode === 'builder' || focus === 'powers';
  const [statusFilter, setStatusFilter] = useState<DelegationStatus | 'all'>(
    powersFocus ? DelegationStatus.ACCEPTED : 'all'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [exporting, setExporting] = useState(false);

  const effectiveComplexId = selectedComplexId;

  const loadAssemblies = useCallback(
    async (complexId?: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await delegationService.getManagerAssemblies(complexId);
        setComplexes(data.complexes);
        setComplexName(data.complexName);
        setAssemblies(data.assemblies);

        if ((mode === 'manager' || mode === 'builder') && data.complexId) {
          setSelectedComplexId(data.complexId);
        }

        if (data.assemblies.length > 0) {
          setSelectedAssemblyId((current) => {
            if (current && data.assemblies.some((assembly) => assembly.id === current)) {
              return current;
            }
            return data.assemblies[0].id;
          });
        } else {
          setSelectedAssemblyId('');
          setDelegations([]);
          setSummary(emptySummary);
        }
      } catch (err: any) {
        setError(err.message || 'No se pudo cargar el contexto de delegaciones');
      } finally {
        setLoading(false);
      }
    },
    [mode]
  );

  const loadDelegations = useCallback(
    async (assemblyId: string, complexId?: string) => {
      if (!assemblyId || !complexId) {
        setDelegations([]);
        setSummary(emptySummary);
        setCurrentPage(1);
        return;
      }

      setLoadingDelegations(true);
      try {
        const data = await delegationService.getManagerAssemblyDelegations(assemblyId, {
          complexId,
        });
        setDelegations(data.delegations);
        setSummary(data.summary);
        setCurrentPage(1);
      } catch (err: any) {
        notifyError(err.message || 'Error al cargar delegaciones');
        setDelegations([]);
        setSummary(emptySummary);
        setCurrentPage(1);
      } finally {
        setLoadingDelegations(false);
      }
    },
    []
  );

  const canPickComplex = mode === 'admin' || mode === 'builder';

  useEffect(() => {
    if (mode === 'manager') {
      loadAssemblies();
    }
  }, [mode, loadAssemblies]);

  useEffect(() => {
    if (canPickComplex && !selectedComplexId) {
      loadAssemblies();
    }
  }, [canPickComplex, selectedComplexId, loadAssemblies]);

  useEffect(() => {
    if (canPickComplex && selectedComplexId) {
      loadAssemblies(selectedComplexId);
    }
  }, [canPickComplex, selectedComplexId, loadAssemblies]);

  useEffect(() => {
    if (selectedAssemblyId && effectiveComplexId) {
      loadDelegations(selectedAssemblyId, effectiveComplexId);
    }
  }, [selectedAssemblyId, effectiveComplexId, loadDelegations]);

  const filteredDelegations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return delegations.filter((delegation) => {
      if (statusFilter !== 'all' && delegation.status !== statusFilter) return false;
      if (!term) return true;

      const delegatorName = `${delegation.delegator?.firstName || ''} ${delegation.delegator?.lastName || ''}`.trim();
      const delegateName = `${delegation.delegate?.firstName || ''} ${delegation.delegate?.lastName || ''}`.trim();
      const delegatorEmail = delegation.delegator?.email || '';
      const delegateEmail = delegation.delegate?.email || '';
      const apartment = delegation.delegator?.profile?.apartment || '';

      return (
        delegatorName.toLowerCase().includes(term) ||
        delegateName.toLowerCase().includes(term) ||
        delegatorEmail.toLowerCase().includes(term) ||
        delegateEmail.toLowerCase().includes(term) ||
        apartment.toLowerCase().includes(term)
      );
    });
  }, [delegations, searchTerm, statusFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredDelegations.length / itemsPerPage)),
    [filteredDelegations.length, itemsPerPage]
  );

  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const paginatedDelegations = useMemo(
    () => filteredDelegations.slice(startIndex, startIndex + itemsPerPage),
    [filteredDelegations, startIndex, itemsPerPage]
  );

  const filteredStatusCounts = useMemo(() => {
    return filteredDelegations.reduce(
      (acc, delegation) => {
        acc[delegation.status] = (acc[delegation.status] || 0) + 1;
        return acc;
      },
      {
        [DelegationStatus.PENDING]: 0,
        [DelegationStatus.ACCEPTED]: 0,
        [DelegationStatus.REJECTED]: 0,
        [DelegationStatus.REVOKED]: 0,
      } as Record<DelegationStatus, number>
    );
  }, [filteredDelegations]);

  const handleRefresh = useCallback(async () => {
    if (mode === 'admin' || mode === 'builder') {
      await loadAssemblies(selectedComplexId || undefined);
    } else {
      await loadAssemblies();
    }
    if (selectedAssemblyId && effectiveComplexId) {
      await loadDelegations(selectedAssemblyId, effectiveComplexId);
    }
  }, [
    mode,
    selectedComplexId,
    loadAssemblies,
    selectedAssemblyId,
    effectiveComplexId,
    loadDelegations,
  ]);

  const handleComplexChange = (complexId: string) => {
    setSelectedComplexId(complexId);
    setSelectedAssemblyId('');
    setDelegations([]);
    setSummary(emptySummary);
    setSearchTerm('');
    setStatusFilter(powersFocus ? DelegationStatus.ACCEPTED : 'all');
    setCurrentPage(1);
  };

  const handleRevoke = useCallback(
    async (delegation: VotingDelegation) => {
      const shouldRevoke = await confirm({
        title: 'Revocar delegación',
        message: `¿Revocar la delegación de ${delegation.delegator?.firstName || ''} ${delegation.delegator?.lastName || ''}?`,
        confirmText: 'Revocar',
        cancelText: 'Cancelar',
        variant: 'warning',
      });

      if (!shouldRevoke) return;

      setRevokingId(delegation.id);
      try {
        await delegationService.revokeDelegationAsManager(delegation.id);
        notifySuccess('Delegación revocada');
        if (selectedAssemblyId && effectiveComplexId) {
          await loadDelegations(selectedAssemblyId, effectiveComplexId);
        }
      } catch (err: any) {
        notifyError(err.message || 'No se pudo revocar la delegación');
      } finally {
        setRevokingId(null);
      }
    },
    [confirm, loadDelegations, selectedAssemblyId, effectiveComplexId]
  );

  const handleDownloadPower = useCallback(async (delegation: VotingDelegation) => {
    setDownloadingId(delegation.id);
    try {
      await delegationService.downloadPowerOfAttorney(delegation.id);
      notifySuccess('Poder descargado');
    } catch (err: any) {
      notifyError(err.message || 'No se pudo descargar el poder');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleExportExcel = useCallback(async () => {
    if (filteredDelegations.length === 0) {
      notifyWarning('No hay delegaciones para exportar con los filtros actuales');
      return;
    }

    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      const rows = filteredDelegations.map((delegation) => ({
        Delegante: `${delegation.delegator?.firstName || ''} ${delegation.delegator?.lastName || ''}`.trim(),
        'Correo delegante': delegation.delegator?.email || '',
        Apartamento: delegation.delegator?.profile?.apartment || '',
        Delegado: `${delegation.delegate?.firstName || ''} ${delegation.delegate?.lastName || ''}`.trim(),
        'Correo delegado': delegation.delegate?.email || '',
        Coeficiente: Number(delegation.delegatedCoefficient || 0),
        Estado: statusConfig[delegation.status].label,
        Creada: formatDateTimeEsCO(delegation.createdAt, ''),
        Aceptada: formatDateTimeEsCO(delegation.acceptedAt, ''),
        Revocada: formatDateTimeEsCO(delegation.revokedAt, ''),
        Rechazada: formatDateTimeEsCO(delegation.rejectedAt, ''),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [
        { wch: 24 },
        { wch: 28 },
        { wch: 18 },
        { wch: 24 },
        { wch: 28 },
        { wch: 14 },
        { wch: 12 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Delegaciones');

      const selectedAssembly = assemblies.find((assembly) => assembly.id === selectedAssemblyId);
      const sanitize = (value: string) =>
        value
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9_-]+/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_|_$/g, '')
          .slice(0, 40) || 'sin_nombre';

      const now = new Date();
      const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(
        now.getDate()
      ).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(
        now.getMinutes()
      ).padStart(2, '0')}`;
      const complexSlug = sanitize(complexName || selectedComplexId || 'conjunto');
      const assemblySlug = sanitize(selectedAssembly?.title || selectedAssemblyId || 'asamblea');

      XLSX.writeFile(workbook, `delegaciones_${complexSlug}_${assemblySlug}_${timestamp}.xlsx`);
      notifySuccess(`Excel generado (${filteredDelegations.length} filas)`);
    } catch (err) {
      console.error('Error exportando delegaciones a Excel:', err);
      notifyError('No se pudo generar el archivo Excel');
    } finally {
      setExporting(false);
    }
  }, [
    filteredDelegations,
    assemblies,
    selectedAssemblyId,
    complexName,
    selectedComplexId,
  ]);

  const delegationRows = useMemo(
    () =>
      paginatedDelegations.map((delegation) => {
        const status = statusConfig[delegation.status];
        const StatusIcon = status.icon;
        const canRevoke =
          mode !== 'builder' &&
          (delegation.status === DelegationStatus.PENDING ||
            delegation.status === DelegationStatus.ACCEPTED);
        const canDownloadPower =
          (mode === 'admin' || mode === 'builder') &&
          delegation.status === DelegationStatus.ACCEPTED;

        return (
          <tr key={delegation.id} className="border-b border-gray-100 hover:bg-gray-50">
            <td className="px-4 py-3 align-top">
              <div className="font-medium text-gray-900">
                {delegation.delegator?.firstName} {delegation.delegator?.lastName}
              </div>
              <div className="text-xs text-gray-500">{delegation.delegator?.email}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {formatUnitChips(delegation.delegator?.profile?.apartment)}
              </div>
            </td>
            <td className="px-4 py-3 align-top">
              <div className="font-medium text-gray-900">
                {delegation.delegate?.firstName} {delegation.delegate?.lastName}
              </div>
              <div className="text-xs text-gray-500">{delegation.delegate?.email}</div>
            </td>
            <td className="px-4 py-3 align-top text-sm text-gray-700">
              {formatCoefficient(delegation.delegatedCoefficient)}
            </td>
            <td className="px-4 py-3 align-top">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}
              >
                <StatusIcon className="h-3.5 w-3.5" />
                {status.label}
              </span>
            </td>
            <td className="px-4 py-3 align-top text-xs text-gray-600">
              <div>Creada: {formatDateTimeEsCO(delegation.createdAt)}</div>
              {delegation.acceptedAt && <div>Aceptada: {formatDateTimeEsCO(delegation.acceptedAt)}</div>}
              {delegation.revokedAt && <div>Revocada: {formatDateTimeEsCO(delegation.revokedAt)}</div>}
            </td>
            <td className="px-4 py-3 align-top">
              <div className="flex flex-col items-start gap-2">
                {canDownloadPower ? (
                  <button
                    type="button"
                    onClick={() => handleDownloadPower(delegation)}
                    disabled={downloadingId === delegation.id}
                    className="inline-flex items-center gap-1 rounded-md border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {downloadingId === delegation.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Descargar poder
                  </button>
                ) : null}
                {canRevoke ? (
                  <button
                    type="button"
                    onClick={() => handleRevoke(delegation)}
                    disabled={revokingId === delegation.id}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    {revokingId === delegation.id ? (
                      <Spinner size="sm" />
                    ) : (
                      <Ban className="h-3.5 w-3.5" />
                    )}
                    Revocar
                  </button>
                ) : !canDownloadPower ? (
                  <span className="text-xs text-gray-400">—</span>
                ) : null}
              </div>
            </td>
          </tr>
        );
      }),
    [paginatedDelegations, revokingId, downloadingId, handleRevoke, handleDownloadPower]
  );

  if (loading) {
    return <LoadingState layout="page" message="Cargando delegaciones..." />;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-800">
          {error}
        </div>
      </div>
    );
  }

  const showContent = mode === 'manager' || Boolean(selectedComplexId);

  return (
    <div className="p-4 pt-16 md:p-8 md:pt-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {powersFocus ? 'Poderes firmados' : 'Delegaciones'}
          </h1>
          <p className="mt-2 text-gray-600">
            {powersFocus
              ? 'Descarga uno a uno los poderes firmados por conjunto y asamblea.'
              : mode === 'admin'
                ? 'Supervisión de poderes delegados por conjunto y asamblea.'
                : 'Gestión de poderes delegados por asamblea.'}
          </p>
        </div>

        <DelegationsContextToolbar
          mode={mode}
          complexName={complexName}
          complexes={complexes}
          selectedComplexId={selectedComplexId}
          onComplexChange={handleComplexChange}
          assemblies={assemblies}
          selectedAssemblyId={selectedAssemblyId}
          onAssemblyChange={setSelectedAssemblyId}
          onRefresh={handleRefresh}
          refreshing={loading || loadingDelegations}
          title={powersFocus ? 'Poderes firmados' : undefined}
          helperText={
            powersFocus
              ? 'Selecciona conjunto y asamblea; descarga el PDF de cada poder aceptado.'
              : undefined
          }
          onExport={handleExportExcel}
          exportDisabled={!showContent || filteredDelegations.length === 0}
          exporting={exporting}
        />

        <AdminFiltersPanel columns={3}>
          <AdminFilterSearch
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            placeholder="Buscar por nombre, correo o apartamento..."
          />

          <AdminFilterSelect
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as DelegationStatus | 'all');
              setCurrentPage(1);
            }}
            aria-label="Filtrar por estado"
          >
            <option value="all">Todos los estados</option>
            <option value={DelegationStatus.PENDING}>Pendiente</option>
            <option value={DelegationStatus.ACCEPTED}>Aceptada</option>
            <option value={DelegationStatus.REJECTED}>Rechazada</option>
            <option value={DelegationStatus.REVOKED}>Revocada</option>
          </AdminFilterSelect>

          <AdminFilterSelect
            value={itemsPerPage}
            onChange={(value) => {
              setItemsPerPage(Number(value));
              setCurrentPage(1);
            }}
            aria-label="Resultados por página"
          >
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </AdminFilterSelect>
        </AdminFiltersPanel>

        {!showContent ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
            Selecciona un conjunto para ver delegaciones y métricas de coeficiente.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4">
                <div className="flex items-center gap-2 text-sm text-indigo-700">
                  <Scale className="h-4 w-4" />
                  Coef. en poderes (aceptados)
                </div>
                <div className="mt-2 text-2xl font-semibold text-indigo-950">
                  {formatCoefficient(summary.totalCoefficientAccepted)}
                </div>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
                <div className="flex items-center gap-2 text-sm text-amber-700">
                  <Clock className="h-4 w-4" />
                  Coef. pendiente
                </div>
                <div className="mt-2 text-2xl font-semibold text-amber-950">
                  {formatCoefficient(summary.totalCoefficientPending)}
                </div>
              </div>
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <Percent className="h-4 w-4" />
                  % del conjunto delegado
                </div>
                <div className="mt-2 text-2xl font-semibold text-blue-950">
                  {summary.delegationCoveragePercent.toLocaleString('es-CO', {
                    maximumFractionDigits: 2,
                  })}
                  %
                </div>
              </div>
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <Handshake className="h-4 w-4" />
                  Poderes activos
                </div>
                <div className="mt-2 text-2xl font-semibold text-emerald-950">
                  {summary.activePowersCount}
                </div>
                <p className="mt-1 text-xs text-emerald-700">
                  {summary.uniqueDelegatorsAccepted} delegantes · {summary.uniqueDelegatesAccepted}{' '}
                  apoderados
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2" aria-label="Conteo por estado">
              {(Object.keys(statusConfig) as DelegationStatus[]).map((status) => {
                const config = statusConfig[status];
                const Icon = config.icon;
                return (
                  <span
                    key={status}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ${config.className}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {config.label}
                    <span className="font-semibold">{filteredStatusCounts[status]}</span>
                  </span>
                );
              })}
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">
                <Users className="h-3.5 w-3.5" />
                Coef. total conjunto
                <span className="font-semibold">{formatCoefficient(summary.complexTotalCoefficient)}</span>
              </span>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {loadingDelegations ? (
                <LoadingState layout="section" message="Cargando delegaciones..." />
              ) : filteredDelegations.length === 0 ? (
                <div className="py-16 text-center text-sm text-gray-500">
                  No hay delegaciones que coincidan con los filtros.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Delegante</th>
                        <th className="px-4 py-3">Delegado</th>
                        <th className="px-4 py-3">Coeficiente</th>
                        <th className="px-4 py-3">Estado</th>
                        <th className="px-4 py-3">Fechas</th>
                        <th className="px-4 py-3">Acción</th>
                      </tr>
                    </thead>
                    <tbody>{delegationRows}</tbody>
                  </table>

                  <div className="border-t border-gray-200 bg-white px-6 py-5">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                      <div className="text-sm text-gray-600">
                        <p className="font-medium text-gray-900">
                          Total: <span className="text-lg text-blue-600">{filteredDelegations.length}</span>{' '}
                          delegaciones
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Mostrando <span className="font-semibold">{startIndex + 1}</span> a{' '}
                          <span className="font-semibold">
                            {Math.min(startIndex + itemsPerPage, filteredDelegations.length)}
                          </span>{' '}
                          (Página <span className="font-semibold">{safeCurrentPage}</span> de{' '}
                          <span className="font-semibold">{totalPages}</span>)
                        </p>
                      </div>

                      {totalPages > 1 ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => setCurrentPage(1)}
                            disabled={safeCurrentPage === 1}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Primera página"
                          >
                            ⟨⟨
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={safeCurrentPage === 1}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Anterior"
                          >
                            ‹
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safeCurrentPage === totalPages}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Siguiente"
                          >
                            ›
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={safeCurrentPage === totalPages}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Última página"
                          >
                            ⟩⟩
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
